
import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';

interface EmailVerificationModalProps {
  open: boolean;
  email: string;
  onVerify: (code: string) => void;
  onResend: () => void;
  onClose?: () => void;
}

export function EmailVerificationModal({
  open,
  email,
  onVerify,
  onResend,
  onClose,
}: EmailVerificationModalProps) {
  const [code, setCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const { toast } = useToast();

  const handleVerify = async () => {
    if (code.length !== 6) {
      toast({
        title: 'Invalid Code',
        description: 'Please enter a 6-digit verification code',
        variant: 'destructive',
      });
      return;
    }

    setIsVerifying(true);
    try {
      await onVerify(code);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResend = () => {
    setCode('');
    onResend();
    toast({
      title: 'Code Resent',
      description: 'A new verification code has been sent to your email',
    });
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose?.()}>
      <DialogContent className="sm:max-w-md" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Verify Your Email</DialogTitle>
          <DialogDescription>
            We've sent a 6-digit verification code to <strong>{email}</strong>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Input
              type="text"
              placeholder="Enter 6-digit code"
              value={code}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                setCode(value);
              }}
              maxLength={6}
              className="text-center text-2xl tracking-widest"
              autoFocus
            />
          </div>
          <div className="flex gap-2">
            <Button
              onClick={handleVerify}
              disabled={code.length !== 6 || isVerifying}
              className="flex-1"
            >
              {isVerifying ? 'Verifying...' : 'Verify'}
            </Button>
            <Button
              onClick={handleResend}
              variant="outline"
              disabled={isVerifying}
            >
              Resend Code
            </Button>
          </div>
          
        </div>
      </DialogContent>
    </Dialog>
  );
}
